import type React from 'react';
import styled from '@emotion/styled';
import { PatientJourneyDimensionType } from '@trinity/chat/enums/conference';
import { TagDetailsSection } from './TagDetailsSection';
import { TagDetailField } from './TagDetailField';
import { TagDetailsPatientJourneyIcon } from './TagDetailsPatientJourneyIcon';
import type { IconName } from './TagDetailsPatientJourneyIcon';
import { TagDetailsDashboardPublishType } from './TagDetailsDashboardPublishType';
import { TagDetailsDashboardDimensionType } from './TagDetailsDashboardDimensionType';
import { TagDetailsDashboardPublishingSettings } from './TagDetailsDashboardPublishingSettings';
import type { DashboardVisibility } from './TagDetailsDashboardPublishingSettings';

export type TagDetailsPatientJourneySectionProps = {
  order: string;
  dashboardPublishType: string | null | undefined;
  onDashboardPublishTypeChange?: (v: string | null) => void;
  patientJourneyIcon: string | null | undefined;
  onPatientJourneyIconChange?: (v: IconName) => void;
  themedColor?: string;
  visibility: DashboardVisibility | null | undefined;
  onVisibilityChange?: (v: DashboardVisibility) => void;
  disablePage?: boolean;
  dashboardDimensionType?: PatientJourneyDimensionType | null;
  onDashboardDimensionTypeChange?: (v: PatientJourneyDimensionType | null) => void;
  /**
   * Slot for the banner image field (e.g. Row.DashboardBannerImage.Row in frontend,
   * or a read-only TagDetailsBannerImage in the chat app).
   * Only rendered when dashboardPublishType is 'Journey Stage'.
   */
  bannerImageNode?: React.ReactNode;
};

export const TagDetailsPatientJourneySection = ({
  order,
  dashboardPublishType,
  onDashboardPublishTypeChange,
  patientJourneyIcon,
  onPatientJourneyIconChange,
  themedColor,
  visibility,
  onVisibilityChange,
  disablePage = false,
  dashboardDimensionType,
  onDashboardDimensionTypeChange,
  bannerImageNode,
}: TagDetailsPatientJourneySectionProps) => (
  <TagDetailsSection title="Patient Journey Details">
    <Grid>
      {/* Row 1 */}
      <TagDetailField label="Order" value={order} />
      <TagDetailsDashboardPublishType
        label="Dashboard Display Type"
        value={dashboardPublishType}
        onChange={onDashboardPublishTypeChange}
      />
      <TagDetailsPatientJourneyIcon
        label="Patient Journey Icon"
        value={patientJourneyIcon}
        onChange={onPatientJourneyIconChange}
        themedColor={themedColor}
      />

      {/* Row 2 */}
      <TagDetailsDashboardPublishingSettings
        label="Dashboard Display Settings"
        values={visibility ?? { dashboard: false, page: false }}
        onChange={onVisibilityChange}
        disablePage={disablePage}
      />

      {/* Conditionally rendered depending on publish type */}
      {dashboardPublishType === 'Journey Stage' && bannerImageNode}
      {dashboardPublishType === 'Journey Dimension' && (
        <TagDetailsDashboardDimensionType
          label="Dimension Type"
          value={dashboardDimensionType}
          onChange={onDashboardDimensionTypeChange}
        />
      )}
    </Grid>
  </TagDetailsSection>
);

TagDetailsPatientJourneySection.displayName = 'TagDetails.PatientJourneySection';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 24px 20px;
`;
